import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, Button, IconButton, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BoltIcon from '@mui/icons-material/Bolt';
import PeopleIcon from '@mui/icons-material/People';
import { motion } from 'framer-motion';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(63, 160, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid rgba(63, 160, 255, 0.15)',
  zIndex: theme.zIndex.appBar,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
      linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
    `,
    backgroundSize: '60px 60px',
    opacity: 0.8,
    zIndex: 2
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '150%',
    height: '150%',
    background: 'radial-gradient(circle at center, rgba(63, 160, 255, 0.06) 0%, transparent 70%)',
    opacity: 0.7,
    zIndex: 1
  }
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(0.5),
  fontSize: '0.75rem',
  position: 'relative',
  padding: '6px 12px',
  borderRadius: '6px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'rgba(63, 160, 255, 0.03)',
  border: '1px solid rgba(63, 160, 255, 0.15)',
  backdropFilter: 'blur(8px)',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -1,
    borderRadius: '6px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.2), rgba(63, 160, 255, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out'
  },
  '&:hover': {
    backgroundColor: 'rgba(63, 160, 255, 0.08)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(63, 160, 255, 0.15)',
    '&::before': {
      opacity: 1
    },
    '& .MuiSvgIcon-root': {
      transform: 'scale(1.1) rotate(5deg)',
      color: theme.palette.primary.main
    }
  },
  '& .MuiSvgIcon-root': {
    transition: 'all 0.3s ease-in-out',
    color: theme.palette.text.primary,
    marginRight: '8px'
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px'
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: '0 2px 10px rgba(63, 160, 255, 0.1)'
  }
}));

interface Section {
  title: string;
  icon: React.ReactNode;
  href: string;
}

const sections: Section[] = [
  { title: 'TEASER', icon: <DescriptionIcon />, href: '#teaser' },
  { title: 'PITCH DECK', icon: <DescriptionIcon />, href: '#next-basket-pitch-deck' },
  { title: 'GROWTH STRATEGY', icon: <TimelineIcon />, href: '#next-basket-growth-strategy' },
  { title: 'COMPARISON', icon: <AnalyticsIcon />, href: '#next-basket-comparison' },
  { title: 'CORPORATE CULTURE', icon: <PeopleIcon />, href: '#next-basket-corporate-culture' },
  { title: 'CONTACT US', icon: <CampaignIcon />, href: '#footer' },
];

const MotionBox = motion(Box);

const Header = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery('(max-width:1100px)');

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const scrollToSection = (event: React.MouseEvent<HTMLElement>, href: string) => {
    event.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      handleClose();
    }
  };
  return (
    <StyledAppBar position="sticky">
      <Toolbar sx={{ gap: 2 }}>
        <Box
          sx={{
            opacity: 0,
            transform: 'scale(0.8)',
            animation: 'fadeInScale 0.6s ease-out forwards',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -4,
              border: '1px solid rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
              animation: 'borderPulse 2s ease-in-out infinite',
            },
            '@keyframes fadeInScale': {
              to: {
                opacity: 1,
                transform: 'scale(1)',
              },
            },
            '@keyframes borderPulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 0.5,
              },
              '50%': {
                transform: 'scale(1.1)',
                opacity: 0,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 0.5,
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              component="img"
              src="/NB-logo.png"
              alt="Next Basket Logo"
              sx={{
                height: { xs: '40px', md: '50px' },
                width: 'auto',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: 'contrast(1.1)',
                '&:hover': {
                  transform: 'scale(1.05) rotate(-2deg)',
                  filter: 'contrast(1.2) brightness(1.1)'
                },
              }}
            />

          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {isMobile ? (
          <>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMenu}
              sx={{ 
                ml: 2, 
                position: 'relative',
                width: 44,
                height: 44,
                color: '#50b5ff',
                border: '1px solid rgba(80, 181, 255, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                zIndex: theme.zIndex.modal + 1,
                pointerEvents: 'auto',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease'
                },
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  color: '#80cfff',
                  backgroundColor: 'transparent',
                  '& .MuiSvgIcon-root': {
                    transform: 'scale(1.1)',
                    filter: 'drop-shadow(0 0 5px rgba(63, 160, 255, 0.5))'
                  }
                },
                '&:active': {
                  transform: 'translateY(1px)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              sx={{ 
                zIndex: theme.zIndex.modal + 2,
                pointerEvents: 'auto'
              }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.5,
                  overflow: 'visible',
                  backgroundColor: 'rgba(20, 53, 77, 0.57)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(80, 181, 255, 0.3)',
                  borderRadius: '15px',
                  minWidth: '200px',
                  filter: 'drop-shadow(0px 4px 20px rgba(63, 160, 255, 0.2))',
                  zIndex: theme.zIndex.modal + 2,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    background: 'linear-gradient(135deg, rgba(80, 181, 255, 0.3), rgba(80, 181, 255, 0.1))',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: '1px solid rgba(80, 181, 255, 0.3)',
                    borderTop: '1px solid rgba(80, 181, 255, 0.3)',
                  },
                },
              }}
            >
              {sections.map((section) => (
                <MenuItem
                  key={section.title}
                  onClick={(e) => scrollToSection(e, section.href)}
                  component="a"
                  href={section.href}
                  sx={{
                    gap: 1.5,
                    color: '#fff',
                    padding: '12px 16px',
                    transition: 'all 0.3s ease',
                    '& .MuiSvgIcon-root': {
                      color: '#50b5ff',
                      fontSize: '1.3rem',
                      transition: 'all 0.3s ease'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(80, 181, 255, 0.1)',
                      '& .MuiSvgIcon-root': {
                        color: '#80cfff',
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  {section.icon}
                  {section.title}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Box sx={{ 
            display: { xs: 'none', lg: 'flex' }, 
            gap: 1, 
            position: 'relative', 
            zIndex: theme.zIndex.appBar + 1,
            pointerEvents: 'auto'
          }}>
            {sections.map((section, index) => (
              <MotionBox
                key={section.title}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <NavButton
                  href={section.href}
                  startIcon={section.icon}
                  onClick={(e) => scrollToSection(e, section.href)}
                >
                  {section.title}
                </NavButton>
              </MotionBox>
            ))}
          </Box>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
